import { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from "./ui/card";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "./ui/tabs";
import { Button } from "./ui/button";
import { Badge } from "./ui/badge";
import { Progress } from "./ui/progress";
import {
  Brain, Activity, TrendingUp, AlertTriangle, CheckCircle,
  Clock, Users, Database, Zap, RefreshCw, BarChart3, Lightbulb,
  Shield, Cpu, Target, Search, Filter, HardDrive
} from 'lucide-react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import { darkOpsTheme } from '../themes/DarkOpsTheme';
import { fetchMetrics } from '../lib/api';
import { safeFallback } from '../lib/production-guard';

interface KnowledgeMetrics {
  totalKnowledgeNodes: number;
  activeSegments: number;
  memoryUtilization: number;
  averageDecayLevel: number;
  knowledgeCreationRate: number;
  knowledgeAccessRate: number;
  collaborationIndex: number;
  systemEfficiency: number;
}

interface SystemAlert {
  id: string;
  type: 'performance' | 'security' | 'maintenance' | 'capacity' | 'quality';
  severity: 'info' | 'warning' | 'error' | 'critical';
  message: string;
  component: string;
  timestamp: string;
  recommended_action: string;
}

interface HealthStatus {
  status: 'healthy' | 'warning' | 'degraded' | 'critical';
  score: number;
  components: {
    knowledgeIndex: { status: string; score: number };
    memory: { status: string; score: number };
    collaboration: { status: string; score: number };
  };
}

interface KnowledgePulseData {
  metrics: KnowledgeMetrics;
  health: HealthStatus;
  alerts: SystemAlert[];
  trends: {
    systemHealth: number[];
    knowledgeIndex: number[];
    collaboration: number[];
    decayPressure: number[];
    timestamps: string[];
  };
  insights: {
    emergingPatterns: string[];
    knowledgeGaps: string[];
    optimizationOpportunities: string[];
    riskFactors: string[];
  };
  recommendations: {
    immediate: string[];
    shortTerm: string[];
    priority: 'low' | 'medium' | 'high' | 'critical';
  };
}

export default function KnowledgePulsePanel() {
  const [pulseData, setPulseData] = useState<KnowledgePulseData | null>(null);
  const [loading, setLoading] = useState(true);
  const [selectedPersona, setSelectedPersona] = useState('G3X');
  const [refreshing, setRefreshing] = useState(false);
  const [activeTab, setActiveTab] = useState('overview');
  const [alertFilter, setAlertFilter] = useState('all');

  // Fallback mock data used when API returns null
  const fallbackData: KnowledgePulseData = {
    metrics: {
      totalKnowledgeNodes: 1247,
      activeSegments: 892,
      memoryUtilization: 73,
      averageDecayLevel: 0.28,
      knowledgeCreationRate: 45,
      knowledgeAccessRate: 167,
      collaborationIndex: 0.84,
      systemEfficiency: 0.91
    },
    health: {
      status: 'healthy',
      score: 91,
      components: {
        knowledgeIndex: { status: 'healthy', score: 87 },
        memory: { status: 'warning', score: 73 },
        collaboration: { status: 'healthy', score: 94 }
      }
    },
    alerts: [
      {
        id: 'alert_001',
        type: 'maintenance',
        severity: 'warning',
        message: 'Memory utilization approaching threshold (73%)',
        component: 'memory_system',
        timestamp: new Date(Date.now() - 300000).toISOString(),
        recommended_action: 'Schedule memory optimization cycle'
      },
      {
        id: 'alert_002',
        type: 'quality',
        severity: 'info',
        message: '2 knowledge segments require attention',
        component: 'temporal_index',
        timestamp: new Date(Date.now() - 600000).toISOString(),
        recommended_action: 'Review aging segments for relevance'
      },
      {
        id: 'alert_003',
        type: 'performance',
        severity: 'warning',
        message: 'Persona Strategist showing reduced collaboration activity',
        component: 'persona_analytics',
        timestamp: new Date(Date.now() - 900000).toISOString(),
        recommended_action: 'Investigate Strategist engagement patterns'
      }
    ],
    trends: {
      systemHealth: [85, 87, 89, 91, 88, 90, 91, 89, 92, 91],
      knowledgeIndex: [82, 84, 86, 87, 85, 87, 89, 87, 88, 87],
      collaboration: [78, 82, 85, 88, 91, 93, 94, 92, 95, 94],
      decayPressure: [32, 30, 28, 26, 29, 27, 25, 28, 26, 28],
      timestamps: Array.from({length: 10}, (_, i) =>
        new Date(Date.now() - (9-i) * 3600000).toISOString()
      )
    },
    insights: {
      emergingPatterns: [
        'Increased activity in security, data, communication modules',
        'High cross-persona collaboration activity detected',
        'Innovation spike detected in 2 persona(s)'
      ],
      knowledgeGaps: [
        'Knowledge gap in configuration - high decay, low maintenance',
        'Underutilized expertise in legacy_support'
      ],
      optimizationOpportunities: [
        'Remove 1 orphaned knowledge segments',
        'Increase mentorship programs to enhance knowledge transfer',
        'Implement accelerated learning pathways'
      ],
      riskFactors: [
        '1 knowledge segments at critical decay risk',
        'Knowledge retention below acceptable threshold'
      ]
    },
    recommendations: {
      immediate: ['Schedule memory optimization cycle'],
      shortTerm: [
        'Review aging segments for relevance',
        'Investigate Strategist engagement patterns',
        'Increase mentorship programs to enhance knowledge transfer'
      ],
      priority: 'medium' as const
    }
  };

  useEffect(() => {
    loadKnowledgePulseData();
    const interval = setInterval(loadKnowledgePulseData, 15000);
    return () => clearInterval(interval);
  }, [selectedPersona]);

  const loadKnowledgePulseData = async () => {
    try {
      const metricsData = await fetchMetrics();

      if (metricsData?.metrics) {
        const m = metricsData.metrics;
        const memoryPct = m.memoryUsage
          ? Math.round((m.memoryUsage.heapUsed / m.memoryUsage.heapTotal) * 100)
          : fallbackData.metrics.memoryUtilization;

        const apiPulse: KnowledgePulseData = {
          metrics: {
            totalKnowledgeNodes: m.totalAgents ?? m.agentCount ?? fallbackData.metrics.totalKnowledgeNodes,
            activeSegments: m.activeAgents ?? m.activeCapsules ?? fallbackData.metrics.activeSegments,
            memoryUtilization: memoryPct,
            averageDecayLevel: m.averageDecayLevel ?? fallbackData.metrics.averageDecayLevel,
            knowledgeCreationRate: m.knowledgeCreationRate ?? fallbackData.metrics.knowledgeCreationRate,
            knowledgeAccessRate: m.knowledgeAccessRate ?? m.requestsPerMinute ?? fallbackData.metrics.knowledgeAccessRate,
            collaborationIndex: m.collaborationIndex ?? fallbackData.metrics.collaborationIndex,
            systemEfficiency: m.systemEfficiency ?? fallbackData.metrics.systemEfficiency,
          },
          health: {
            status: memoryPct > 90 ? 'critical' : memoryPct > 80 ? 'warning' : 'healthy',
            score: Math.max(0, 100 - memoryPct),
            components: {
              knowledgeIndex: { status: 'healthy', score: 87 },
              memory: {
                status: memoryPct > 80 ? 'warning' : 'healthy',
                score: 100 - memoryPct,
              },
              collaboration: { status: 'healthy', score: 94 },
            },
          },
          alerts: metricsData.alerts || fallbackData.alerts,
          trends: metricsData.trends || fallbackData.trends,
          insights: metricsData.insights || fallbackData.insights,
          recommendations: metricsData.recommendations || fallbackData.recommendations,
        };

        setPulseData(apiPulse);
      } else {
        // API returned null, use fallback (dev only)
        setPulseData(prev => prev || safeFallback(fallbackData));
      }

      setLoading(false);
    } catch (error) {
      console.error('Failed to load knowledge pulse data:', error);
      setPulseData(prev => prev || safeFallback(fallbackData));
      setLoading(false);
    }
  };

  const handleRefresh = async () => {
    setRefreshing(true);
    await loadKnowledgePulseData();
    setRefreshing(false);
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'healthy': return 'text-ag-active';
      case 'warning': return 'text-ag-warning';
      case 'degraded': return 'text-orange-400';
      case 'critical': return 'text-ag-critical';
      default: return 'text-[#A8A29E]';
    }
  };

  const getSeverityColor = (severity: string) => {
    switch (severity) {
      case 'info': return 'bg-[#60A5FA]';
      case 'warning': return 'bg-ag-warning';
      case 'error': return 'bg-orange-500';
      case 'critical': return 'bg-ag-critical';
      default: return 'bg-gray-500';
    }
  };

  const filteredAlerts = pulseData?.alerts?.filter(alert => 
    alertFilter === 'all' || alert.severity === alertFilter
  ) || [];

  if (loading) {
    return (
      <div className="p-6 space-y-6">
        <div className="flex items-center gap-2">
          <RefreshCw className="w-6 h-6 animate-spin text-cyan-400" />
          <h2 className="text-2xl font-semibold">Loading Knowledge Pulse...</h2>
        </div>
      </div>
    );
  }

  if (!pulseData) {
    return (
      <div className="p-6 space-y-6">
        <div className="flex items-center gap-2 text-ag-critical">
          <AlertTriangle className="w-6 h-6" />
          <h2 className="text-2xl font-semibold">Failed to Load Knowledge Pulse Data</h2>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6" style={{ backgroundColor: darkOpsTheme.colors.background }}>
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Brain className="w-8 h-8 text-cyan-400" />
          <h2 className="text-3xl font-bold text-white">Knowledge Pulse Monitor</h2>
          <Badge variant="outline" className="ml-2 text-cyan-400 border-cyan-400">
            Live
          </Badge>
        </div>
        <div className="flex items-center gap-4">
          <select
            value={selectedPersona}
            onChange={(e) => setSelectedPersona(e.target.value)}
            className="bg-[#292524] border border-[#3A352F] text-white px-3 py-2 rounded"
          >
            <option value="G1X">Analyst</option>
            <option value="G2X">Builder</option>
            <option value="G3X">Operator</option>
            <option value="G4X">Strategist</option>
          </select>
          <Button 
            onClick={handleRefresh}
            disabled={refreshing}
            className="bg-cyan-600 hover:bg-cyan-700"
          >
            {refreshing ? (
              <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
            ) : (
              <RefreshCw className="w-4 h-4 mr-2" />
            )}
            Refresh
          </Button>
        </div>
      </div>

      {/* System Health Overview */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card className="bg-gray-900 border-[#3A352F]">
          <CardHeader className="pb-2">
            <CardTitle className="flex items-center gap-2 text-lg">
              <Shield className="w-5 h-5 text-cyan-400" />
              System Health
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <span className="text-3xl font-bold text-white">{pulseData.health.score}</span>
                <Badge className={getStatusColor(pulseData.health.status)}>
                  {pulseData.health.status.toUpperCase()}
                </Badge>
              </div>
              <Progress value={pulseData.health.score} className="h-2" />
              <p className="text-sm text-[#A8A29E]">Overall system efficiency</p>
            </div>
          </CardContent>
        </Card>

        <Card className="bg-gray-900 border-[#3A352F]">
          <CardHeader className="pb-2">
            <CardTitle className="flex items-center gap-2 text-lg">
              <Database className="w-5 h-5 text-green-400" />
              Knowledge Index
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <span className="text-3xl font-bold text-white">{pulseData.metrics.totalKnowledgeNodes}</span>
                <TrendingUp className="w-5 h-5 text-green-400" />
              </div>
              <p className="text-sm text-[#A8A29E]">Total knowledge nodes</p>
              <p className="text-xs text-ag-active">{pulseData.metrics.activeSegments} active segments</p>
            </div>
          </CardContent>
        </Card>

        <Card className="bg-gray-900 border-[#3A352F]">
          <CardHeader className="pb-2">
            <CardTitle className="flex items-center gap-2 text-lg">
              <HardDrive className="w-5 h-5 text-yellow-400" />
              Memory Usage
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <span className="text-3xl font-bold text-white">{pulseData.metrics.memoryUtilization}%</span>
                <Badge variant="secondary" className="text-yellow-400">
                  {pulseData.health.components.memory.status.toUpperCase()}
                </Badge>
              </div>
              <Progress value={pulseData.metrics.memoryUtilization} className="h-2" />
              <p className="text-sm text-[#A8A29E]">Memory utilization</p>
            </div>
          </CardContent>
        </Card>

        <Card className="bg-gray-900 border-[#3A352F]">
          <CardHeader className="pb-2">
            <CardTitle className="flex items-center gap-2 text-lg">
              <Users className="w-5 h-5 text-purple-400" />
              Collaboration
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <span className="text-3xl font-bold text-white">{Math.round(pulseData.metrics.collaborationIndex * 100)}</span>
                <TrendingUp className="w-5 h-5 text-purple-400" />
              </div>
              <Progress value={pulseData.metrics.collaborationIndex * 100} className="h-2" />
              <p className="text-sm text-[#A8A29E]">Cross-persona activity</p>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Main Content Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-4">
        <TabsList className="grid w-full grid-cols-5 bg-[#292524]">
          <TabsTrigger value="overview" className="data-[state=active]:bg-[#C2714F]">
            <BarChart3 className="w-4 h-4 mr-2" />
            Overview
          </TabsTrigger>
          <TabsTrigger value="trends" className="data-[state=active]:bg-[#C2714F]">
            <TrendingUp className="w-4 h-4 mr-2" />
            Trends
          </TabsTrigger>
          <TabsTrigger value="alerts" className="data-[state=active]:bg-[#C2714F]">
            <AlertTriangle className="w-4 h-4 mr-2" />
            Alerts
          </TabsTrigger>
          <TabsTrigger value="insights" className="data-[state=active]:bg-[#C2714F]">
            <Lightbulb className="w-4 h-4 mr-2" />
            Insights
          </TabsTrigger>
          <TabsTrigger value="decay" className="data-[state=active]:bg-[#C2714F]">
            <Clock className="w-4 h-4 mr-2" />
            Decay Analysis
          </TabsTrigger>
        </TabsList>

        <TabsContent value="overview" className="space-y-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Knowledge Flow Metrics */}
            <Card className="bg-gray-900 border-[#3A352F]">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Activity className="w-5 h-5 text-cyan-400" />
                  Knowledge Flow Metrics
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div className="text-center">
                    <div className="text-2xl font-bold text-cyan-400">{pulseData.metrics.knowledgeCreationRate}</div>
                    <div className="text-sm text-[#A8A29E]">Creation Rate</div>
                  </div>
                  <div className="text-center">
                    <div className="text-2xl font-bold text-green-400">{pulseData.metrics.knowledgeAccessRate}</div>
                    <div className="text-sm text-[#A8A29E]">Access Rate</div>
                  </div>
                </div>
                <div className="space-y-2">
                  <div className="flex justify-between text-sm">
                    <span>System Efficiency</span>
                    <span>{Math.round(pulseData.metrics.systemEfficiency * 100)}%</span>
                  </div>
                  <Progress value={pulseData.metrics.systemEfficiency * 100} className="h-2" />
                </div>
                <div className="space-y-2">
                  <div className="flex justify-between text-sm">
                    <span>Decay Level</span>
                    <span>{Math.round(pulseData.metrics.averageDecayLevel * 100)}%</span>
                  </div>
                  <Progress value={pulseData.metrics.averageDecayLevel * 100} className="h-2" />
                </div>
              </CardContent>
            </Card>

            {/* Component Health */}
            <Card className="bg-gray-900 border-[#3A352F]">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Cpu className="w-5 h-5 text-cyan-400" />
                  Component Health
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                {Object.entries(pulseData.health.components).map(([component, health]) => (
                  <div key={component} className="space-y-2">
                    <div className="flex justify-between items-center">
                      <span className="text-sm font-medium capitalize">
                        {component.replace(/([A-Z])/g, ' $1').trim()}
                      </span>
                      <div className="flex items-center gap-2">
                        <span className="text-sm">{health.score}%</span>
                        <Badge variant="outline" className={getStatusColor(health.status)}>
                          {health.status}
                        </Badge>
                      </div>
                    </div>
                    <Progress value={health.score} className="h-2" />
                  </div>
                ))}
              </CardContent>
            </Card>
          </div>

          {/* Recent Activity */}
          <Card className="bg-gray-900 border-[#3A352F]">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Activity className="w-5 h-5 text-cyan-400" />
                Recent Knowledge Activity
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                <div className="flex items-center justify-between p-3 bg-[#292524] rounded-lg">
                  <div className="flex items-center gap-3">
                    <div className="w-2 h-2 bg-green-400 rounded-full"></div>
                    <span className="text-sm">SecurityAuditModule executed successfully</span>
                  </div>
                  <span className="text-xs text-[#A8A29E]">2 min ago</span>
                </div>
                <div className="flex items-center justify-between p-3 bg-[#292524] rounded-lg">
                  <div className="flex items-center gap-3">
                    <div className="w-2 h-2 bg-blue-400 rounded-full"></div>
                    <span className="text-sm">Knowledge segment "security_policies" accessed</span>
                  </div>
                  <span className="text-xs text-[#A8A29E]">5 min ago</span>
                </div>
                <div className="flex items-center justify-between p-3 bg-[#292524] rounded-lg">
                  <div className="flex items-center gap-3">
                    <div className="w-2 h-2 bg-purple-400 rounded-full"></div>
                    <span className="text-sm">Operator-Strategist collaboration initiated</span>
                  </div>
                  <span className="text-xs text-[#A8A29E]">8 min ago</span>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="trends" className="space-y-6">
          <Card className="bg-gray-900 border-[#3A352F]">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <TrendingUp className="w-5 h-5 text-cyan-400" />
                System Health Trends (Last 10 Hours)
              </CardTitle>
            </CardHeader>
            <CardContent>
              <ResponsiveContainer width="100%" height={300}>
                <LineChart data={pulseData.trends.systemHealth.map((value, index) => {
                  const timestamp = pulseData.trends.timestamps[index];
                  const decayPressure = pulseData.trends.decayPressure[index];
                  return {
                    time: timestamp ? new Date(timestamp).getHours() + ':00' : '',
                    systemHealth: value,
                    knowledgeIndex: pulseData.trends.knowledgeIndex[index],
                    collaboration: pulseData.trends.collaboration[index],
                    decayPressure: decayPressure !== undefined ? 100 - decayPressure : 0, // Invert for better visualization
                  };
                })}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#374151" />
                  <XAxis dataKey="time" stroke="#9CA3AF" />
                  <YAxis stroke="#9CA3AF" />
                  <Tooltip 
                    contentStyle={{ 
                      backgroundColor: '#1F2937', 
                      border: '1px solid #374151',
                      borderRadius: '8px'
                    }}
                  />
                  <Line type="monotone" dataKey="systemHealth" stroke="#00f5ff" strokeWidth={2} name="System Health" />
                  <Line type="monotone" dataKey="knowledgeIndex" stroke="#10b981" strokeWidth={2} name="Knowledge Index" />
                  <Line type="monotone" dataKey="collaboration" stroke="#8b5cf6" strokeWidth={2} name="Collaboration" />
                  <Line type="monotone" dataKey="decayPressure" stroke="#f59e0b" strokeWidth={2} name="Freshness" />
                </LineChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="alerts" className="space-y-6">
          <Card className="bg-gray-900 border-[#3A352F]">
            <CardHeader>
              <CardTitle className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <AlertTriangle className="w-5 h-5 text-cyan-400" />
                  System Alerts
                </div>
                <div className="flex items-center gap-2">
                  <Filter className="w-4 h-4 text-[#A8A29E]" />
                  <select
                    value={alertFilter}
                    onChange={(e) => setAlertFilter(e.target.value)}
                    className="bg-[#292524] border border-[#3A352F] text-white px-2 py-1 rounded text-sm"
                  >
                    <option value="all">All Alerts</option>
                    <option value="critical">Critical</option>
                    <option value="warning">Warning</option>
                    <option value="error">Error</option>
                    <option value="info">Info</option>
                  </select>
                </div>
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                {filteredAlerts.length === 0 ? (
                  <div className="text-center py-8 text-[#A8A29E]">
                    <CheckCircle className="w-12 h-12 mx-auto mb-2" />
                    <p>No alerts matching filter criteria</p>
                  </div>
                ) : (
                  filteredAlerts.map((alert) => (
                    <div key={alert.id} className="p-4 bg-[#292524] rounded-lg border-l-4 border-l-orange-500">
                      <div className="flex items-start justify-between">
                        <div className="space-y-1 flex-1">
                          <div className="flex items-center gap-2">
                            <div className={`w-2 h-2 rounded-full ${getSeverityColor(alert.severity)}`}></div>
                            <Badge variant="outline" className="text-xs">
                              {alert.type.toUpperCase()}
                            </Badge>
                            <Badge variant="outline" className={`text-xs ${getStatusColor(alert.severity)}`}>
                              {alert.severity.toUpperCase()}
                            </Badge>
                          </div>
                          <p className="text-sm font-medium text-white">{alert.message}</p>
                          <p className="text-xs text-[#A8A29E]">Component: {alert.component}</p>
                          <p className="text-xs text-cyan-400">Action: {alert.recommended_action}</p>
                        </div>
                        <span className="text-xs text-[#A8A29E]">
                          {new Date(alert.timestamp).toLocaleTimeString()}
                        </span>
                      </div>
                    </div>
                  ))
                )}
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="insights" className="space-y-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <Card className="bg-gray-900 border-[#3A352F]">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Lightbulb className="w-5 h-5 text-yellow-400" />
                  Emerging Patterns
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {pulseData.insights.emergingPatterns.map((pattern, index) => (
                    <div key={index} className="p-3 bg-[#292524] rounded-lg flex items-start gap-3">
                      <TrendingUp className="w-4 h-4 text-green-400 mt-1 flex-shrink-0" />
                      <span className="text-sm text-white">{pattern}</span>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>

            <Card className="bg-gray-900 border-[#3A352F]">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Target className="w-5 h-5 text-orange-400" />
                  Optimization Opportunities
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {pulseData.insights.optimizationOpportunities.map((opportunity, index) => (
                    <div key={index} className="p-3 bg-[#292524] rounded-lg flex items-start gap-3">
                      <Zap className="w-4 h-4 text-yellow-400 mt-1 flex-shrink-0" />
                      <span className="text-sm text-white">{opportunity}</span>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>

            <Card className="bg-gray-900 border-[#3A352F]">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Search className="w-5 h-5 text-blue-400" />
                  Knowledge Gaps
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {pulseData.insights.knowledgeGaps.map((gap, index) => (
                    <div key={index} className="p-3 bg-[#292524] rounded-lg flex items-start gap-3">
                      <AlertTriangle className="w-4 h-4 text-blue-400 mt-1 flex-shrink-0" />
                      <span className="text-sm text-white">{gap}</span>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>

            <Card className="bg-gray-900 border-[#3A352F]">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <AlertTriangle className="w-5 h-5 text-red-400" />
                  Risk Factors
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {pulseData.insights.riskFactors.map((risk, index) => (
                    <div key={index} className="p-3 bg-[#292524] rounded-lg flex items-start gap-3">
                      <Shield className="w-4 h-4 text-red-400 mt-1 flex-shrink-0" />
                      <span className="text-sm text-white">{risk}</span>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Recommendations */}
          <Card className="bg-gray-900 border-[#3A352F]">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <CheckCircle className="w-5 h-5 text-cyan-400" />
                Recommendations
                <Badge variant="outline" className={`ml-2 ${
                  pulseData.recommendations.priority === 'critical' ? 'text-ag-critical border-ag-critical' :
                  pulseData.recommendations.priority === 'high' ? 'text-orange-400 border-orange-400' :
                  pulseData.recommendations.priority === 'medium' ? 'text-ag-warning border-ag-warning' :
                  'text-ag-active border-ag-active'
                }`}>
                  {pulseData.recommendations.priority.toUpperCase()} PRIORITY
                </Badge>
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                {pulseData.recommendations.immediate.length > 0 && (
                  <div>
                    <h4 className="text-sm font-semibold text-ag-critical mb-2">Immediate Actions Required</h4>
                    <div className="space-y-2">
                      {pulseData.recommendations.immediate.map((action, index) => (
                        <div key={index} className="p-2 bg-red-900/20 border border-red-800 rounded text-sm">
                          {action}
                        </div>
                      ))}
                    </div>
                  </div>
                )}
                
                {pulseData.recommendations.shortTerm.length > 0 && (
                  <div>
                    <h4 className="text-sm font-semibold text-ag-warning mb-2">Short-term Improvements</h4>
                    <div className="space-y-2">
                      {pulseData.recommendations.shortTerm.map((action, index) => (
                        <div key={index} className="p-2 bg-yellow-900/20 border border-yellow-800 rounded text-sm">
                          {action}
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="decay" className="space-y-6">
          <Card className="bg-gray-900 border-[#3A352F]">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Clock className="w-5 h-5 text-cyan-400" />
                Temporal Decay Analysis
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
                <div className="text-center p-4 bg-[#292524] rounded-lg">
                  <div className="text-2xl font-bold text-ag-active">75%</div>
                  <div className="text-sm text-[#A8A29E]">Fresh Segments</div>
                </div>
                <div className="text-center p-4 bg-[#292524] rounded-lg">
                  <div className="text-2xl font-bold text-ag-warning">20%</div>
                  <div className="text-sm text-[#A8A29E]">Aging Segments</div>
                </div>
                <div className="text-center p-4 bg-[#292524] rounded-lg">
                  <div className="text-2xl font-bold text-orange-400">4%</div>
                  <div className="text-sm text-[#A8A29E]">Stale Segments</div>
                </div>
                <div className="text-center p-4 bg-[#292524] rounded-lg">
                  <div className="text-2xl font-bold text-ag-critical">1%</div>
                  <div className="text-sm text-[#A8A29E]">Critical Decay</div>
                </div>
              </div>
              
              <div className="text-center text-[#A8A29E]">
                <p>Detailed decay analysis requires dedicated temporal monitoring system.</p>
                <p className="text-xs mt-1">Access full temporal analytics via Knowledge Pulse API.</p>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}